import React from 'react'
import { useTranslation } from 'react-i18next'
import { Dropdown, DropdownProps, Menu } from 'semantic-ui-react'
import { StrictMenuProps } from 'semantic-ui-react/dist/commonjs/collections/Menu'
import { Tooltip } from '../form/Tooltip'

export interface IPaginationMenuItem {
  icon?: string
  text?: string
  title?: React.ReactNode
  action?: () => void
}

export interface IPaginationProps extends Pick<StrictMenuProps, 'attached'> {
  total: number
  totalPages: number
  currentPage: number
  limit: number
  menuItems?: IPaginationMenuItem[]

  onPageChange: (pageNumber: number) => void
  onLimitChange?: (limit: number) => void
}

export type PaginationProps = IPaginationProps

interface ILimitDropdownProps extends Pick<IPaginationProps, 'limit'>, DropdownProps {}

const resultLimitOptions = [
  {
    text: '25',
    value: 25
  },
  {
    text: '50',
    value: 50
  },
  {
    text: '100',
    value: 100
  }
]

export const LimitDropdown = ({ limit, onChange }: ILimitDropdownProps) => {
  const { t } = useTranslation()
  return (
    <Tooltip
      content={t('pagination.tooltip_page_size')}
      trigger={<Dropdown trigger={limit} item value={limit} onChange={onChange} options={resultLimitOptions} />}
    />
  )
}

export const Pagination: React.FC<PaginationProps> = props => {
  const { t } = useTranslation()
  const { total, currentPage, totalPages, limit, onPageChange, onLimitChange, menuItems, attached } = props

  const canLoadPreviousPage = currentPage > 1
  const canLoadNextPage = currentPage < totalPages

  return (
    <Menu icon attached={attached}>
      {menuItems && menuItems.length > 0 && (
        <Menu.Menu position='left'>
          {menuItems.map((item, i) => {
            const menuItem = (
              <Menu.Item key={i} link onClick={item.action} icon={item.icon}>
                {item.text}
              </Menu.Item>
            )
            if (item.title) {
              return <Tooltip key={i} content={item.title} trigger={menuItem} />
            }

            return menuItem
          })}
        </Menu.Menu>
      )}
      <Menu.Menu position='right'>
        {onLimitChange && (
          <LimitDropdown limit={limit} onChange={(_, { value }) => onLimitChange(value as number)}>
            {limit}
          </LimitDropdown>
        )}
        <Menu.Item>
          {total} {t('pagination.results')}
        </Menu.Item>
        <Menu.Item data-testid='pagination__current_of_total'>
          {currentPage} / {totalPages}
        </Menu.Item>
        <Tooltip
          content={t('pagination.tooltip_first_page')}
          trigger={
            <Menu.Item
              data-testid='pagination__button-first-page'
              link
              icon='angle double left'
              onClick={() => onPageChange(1)}
              disabled={!canLoadPreviousPage}
            />
          }
        />
        <Tooltip
          content={t('pagination.tooltip_prev_page')}
          trigger={
            <Menu.Item
              data-testid='pagination__button-previous'
              link
              icon='angle left'
              onClick={() => onPageChange(currentPage - 1)}
              disabled={!canLoadPreviousPage}
            />
          }
        />
        <Tooltip
          content={t('pagination.tooltip_next_page')}
          trigger={
            <Menu.Item
              data-testid='pagination__button-next'
              link
              icon='angle right'
              onClick={() => onPageChange(currentPage + 1)}
              disabled={!canLoadNextPage}
            />
          }
        />
        <Tooltip
          content={t('pagination.tooltip_last_page')}
          trigger={
            <Menu.Item
              data-testid='pagination__button-last-page'
              link
              icon='angle double right'
              onClick={() => onPageChange(totalPages)}
              disabled={!canLoadNextPage}
            />
          }
        />
      </Menu.Menu>
    </Menu>
  )
}

Pagination.displayName = 'Pagination'

export default React.memo(Pagination)
